import { clampToPayoutCeiling } from './crashPayoutCeiling'
import {
  generateChipsFromMultipliers,
  generateSmartChips,
  hasCustomChipMultipliers,
  MIN_BET_CHIP_MIN_VALUE,
  prependMinBetChip
} from 'src/utils/generateSmartChips'
import { CHIP_STYLES } from './chipStack'

export type Zone = 'left' | 'right'
export interface ZoneStakes {
  left: number
  right: number
}
export interface StakeLimits {
  minBet: number
  maxBet: number
  balance: number
  // Decimal precision of the currency (BTC=8, USD=2, …). Rounding to fewer
  // decimals than the currency supports drops sub-unit chips for crypto.
  precision: number
}

// Round to the currency's decimal precision (was hardcoded to 2 decimals,
// which zeroed sub-cent crypto chips like 0.0005 on an 8-dec currency).
export const roundTo = (n: number, precision: number): number => {
  const f = 10 ** precision
  return Math.round(n * f) / f
}

export const emptyStakes = (): ZoneStakes => ({ left: 0, right: 0 })

// The zones' combined stake, rounded at the currency precision (the same
// precision the per-add rounding in clampChipToZone uses). A raw
// `left + right` float sum carries IEEE-754 dust — 0.10 + 7.60 ===
// 7.699999999999999 — and the floor-based display then eats the cent: the
// "Total staked" pill showed 7.69 for a real 7.70 stake.
export const totalStaked = (s: ZoneStakes, precision: number): number =>
  roundTo(s.left + s.right, precision)

export const zoneToSlotIndex = (z: Zone): 0 | 1 => (z === 'right' ? 1 : 0)

// ── Panel mode (chip-placement collapse model, v5) ───────────────────────
// Presentation-only state machine for the whole dock. Mirrors the reference
// ChipPlacementPanel's `collapseToPlacedBets` model:
//
//   betting        — betting window: the full panel (zones + strip).
//   live-collapsed — round live AND the player has bets riding: ONLY the
//                    staked zones survive (locked stakes → cash-out bars);
//                    the chip strip folds away. No ghost pills.
//   live-full      — round live, NO bets: the normal full betting panel —
//                    builder cards wear the in-card amber NEXT ROUND eyebrow
//                    (v12.1; the floating pill is gone), chips are
//                    immediately usable, placed stakes queue and auto-submit
//                    at the next round start (the hook's single-shot
//                    pipeline).
export type CrashPanelMode = 'betting' | 'live-full' | 'live-collapsed'

export interface ZoneFlags {
  left: boolean
  right: boolean
}

export function resolvePanelMode(isBettingOpen: boolean, riding: ZoneFlags): CrashPanelMode {
  if (isBettingOpen) return 'betting'
  return riding.left || riding.right ? 'live-collapsed' : 'live-full'
}

// NOTE (v12.1, tweak 3): zone GHOSTING is gone — `isZoneVisible` with it.
// Every zone always holds its slot and renders: a zone with no bet this
// round is a live next-round builder ("Tap to bet" + the in-card NEXT ROUND
// label), behaving exactly like the post-cashout builder. One mental model:
// any non-riding, non-busted zone is always a door to the next round.

// ── Zone riding (v12 — R7) ───────────────────────────────────────────────
// THE per-zone "is a bet on the wire" fact. A zone rides while a
// non-terminal bet exists — cancelled frees it (refund), and v12: a CASHED
// bet (won / cashed_out) frees it too, converting the card to the
// next-round builder. Without a store bet, a fired submission
// (submittedStake > 0, POST in flight / placing) still counts as riding so
// the placing card survives the live collapse.
const RIDING_FREE_STATUSES = ['cancelled', 'won', 'cashed_out']

export function resolveZoneRiding(
  bet: { status: string } | undefined,
  submittedStake: number
): boolean {
  if (bet !== undefined) return !RIDING_FREE_STATUSES.includes(bet.status)
  return submittedStake > 0
}

// ── ONE zone-card state machine (v6) ─────────────────────────────────────
// A staked zone renders ONE card with fixed geometry through the whole
// round; only its CONTENT changes. This resolver is the single source of
// truth for which content the card shows — it mirrors (and replaces) the
// branch ladder that used to live in CrashBetZone, so the mapping is
// testable as a pure function.
//
//   empty   — no stake, no bet: the "Tap to bet" target.
//   staked  — chips placed, betting open (chip stack + amount).
//   queued  — chips placed while a round is LIVE: next-round stake
//             ("Next round" eyebrow + ✕ to clear).
//   placing — submission fired, POST in flight, no bet id yet.
//   locked  — bet accepted, pre-climb ("Bet placed" — the anchor state).
//   live    — climbing (or crash-hold freeze): the tappable cash-out card.
//   busted  — the crash is visible on this viewer's frames and the bet
//             lost (or never resolved): "Busted −X @ Yx".
//
// v12: there is NO 'won' card state — a cashed bet FREES the zone (branch
// A below) into the builder family; the win renders as the derived banner
// above the zone (resolveZoneWinBanner).
//
// cashing — the OPTIMISTIC settling card: a cash-out tap passed the arming
// gate and the POST is in flight. Held in the hook, never in the store, so
// the chime/banner (store-derived) can only fire on server confirm.
export type ZoneCardState =
  | 'empty'
  | 'staked'
  | 'queued'
  | 'placing'
  | 'locked'
  | 'live'
  | 'cashing'
  | 'busted'

// Statuses that mean a bet is settled one way or another.
const CARD_RESOLVED_STATUSES = ['won', 'lost', 'cashed_out', 'cancelled']
const CARD_CASHED_STATUSES = ['won', 'cashed_out']

/**
 * The combined stake still riding the current round (the stats overlay's
 * "In play" figure): every zone bet not yet settled, summed then rounded at
 * the currency precision — the raw reduce carries the same IEEE-754 dust as
 * totalStaked (0.1 + 7.6 === 7.699999999999999 → "7.69" on the pill).
 */
export function totalInPlay(
  bets: Array<{ status: string; betAmount: number } | undefined>,
  precision: number
): number {
  const sum = bets.reduce(
    (acc, bet) => (bet && !CARD_RESOLVED_STATUSES.includes(bet.status) ? acc + bet.betAmount : acc),
    0
  )
  return roundTo(sum, precision)
}

/**
 * The bottom-left overlay's "Potential win" readout (Kasper note c) — what
 * the player's OWN round could pay right now:
 *
 *   idle   — nothing to say: no bets, betting still open ("—"), a bust with
 *            nothing banked, or a refunded round.
 *   riding — money still on the line while the multiplier climbs. The
 *            OVERLAY multiplies `activeStake` by the live DISPLAY multiplier
 *            per frame (this stays pure — no clock in here) and adds
 *            `lockedPayout`, so a zone that already cashed keeps its banked
 *            winnings in the figure while the other zone keeps counting.
 *   won    — every stake is resolved and something was banked: the combined
 *            realized payout (a cashed bet's potentialPayout IS its realized
 *            amount — the store overwrites it at cashout, same source the
 *            zone win banner shows).
 *
 * `crashSeen` MUST be the VISUAL crash (the viewer's frames), never the event
 * clock: the riding→won/idle settle is exactly the kind of early tell every
 * other crash surface gates. Until the bust is on screen the readout keeps
 * riding on the (held) display multiplier.
 *
 * THE CRASH-HOLD (review fix — this is where the tell actually hid): the
 * moment crash:occurred lands, the websocket handler flips every pending bet
 * to 'lost' AND appends a synthetic race_crashed (which collapses
 * `climbing`) — both on the EVENT clock, ~1.5-2s before the frames. Without
 * `crashKnown`, those two doors settled the readout early on EVERY crashed
 * round and the crashSeen gate was unreachable. While crashKnown && !crashSeen
 * the readout must keep RIDING — 'lost' stakes still count as active and the
 * collapsed `climbing` is ignored — mirroring resolveZoneCardState's
 * inCrashHold. A CANCELLED round never sets crashKnown, so refunds settle to
 * idle immediately (no hold on a void).
 */
export type PotentialWinReadout =
  | { kind: 'idle' }
  // v49 (Aviator parity): staged stakes with an auto-cashout target show
  // their would-be win BEFORE the round — stake × target, static.
  | { kind: 'preview'; amount: number }
  // activeStakes is PER BET, never pre-summed: the backend liability ceiling is
  // applied to EACH bet independently (register_bet.go computeMaxPayout has no
  // round/player aggregation), so a two-zone player can be paid up to 2x it.
  // Clamping a pre-summed total would under-report by up to 2x.
  | { kind: 'riding'; lockedPayout: number; activeStakes: number[] }
  | { kind: 'won'; amount: number }

/**
 * The bet-time preview (v49): Σ stake × auto-cashout target over zones
 * whose cash-out is armed. Zones with cash-out OFF contribute nothing —
 * their win has no defined target until the player taps out live. 0 means
 * "no preview" (the pill stays on the idle dash).
 */
export function resolveBettingPreviewWin(
  stakes: ZoneStakes,
  auto: Record<Zone, { cashoutEnabled: boolean; cashoutMultiplier: number }>,
  payoutCeiling: number | null = null
): number {
  let sum = 0
  for (const zone of ['left', 'right'] as Zone[]) {
    const stake = stakes[zone]
    const terms = auto[zone]
    if (stake > 0 && terms.cashoutEnabled && terms.cashoutMultiplier > 1) {
      // Clamp PER ZONE: each bet carries its own ceiling, so two zones can each
      // be paid up to it. Clamping the sum would under-promise a payable win.
      sum += clampToPayoutCeiling(stake * terms.cashoutMultiplier, payoutCeiling)
    }
  }
  return sum
}

export function resolvePotentialWin(
  bets: Array<{ status: string; betAmount: number; potentialPayout: number } | undefined>,
  climbing: boolean,
  crashSeen: boolean,
  crashKnown = false
): PotentialWinReadout {
  const holding = crashKnown && !crashSeen
  let lockedPayout = 0
  const activeStakes: number[] = []
  let any = false
  for (const bet of bets) {
    if (!bet) continue
    any = true
    if (CARD_CASHED_STATUSES.includes(bet.status)) {
      // Realized — the backend already paid this. NEVER clamped downstream.
      lockedPayout += bet.potentialPayout
    } else if (!CARD_RESOLVED_STATUSES.includes(bet.status)) {
      activeStakes.push(bet.betAmount)
    } else if (holding && bet.status === 'lost') {
      // Dead on the event clock but NOT on the viewer's frames yet — keeps
      // riding through the hold (same 'lost' arm as inCrashHold).
      activeStakes.push(bet.betAmount)
    }
    // cancelled (refunds) — and, outside the hold, lost — add nothing.
  }
  if (!any) return { kind: 'idle' }
  if (holding && activeStakes.length > 0) {
    // Known-but-unseen crash with money that rode into it: stay riding.
    // The held display clock freezes the figure alongside the hero.
    return { kind: 'riding', lockedPayout, activeStakes }
  }
  if (activeStakes.length === 0 || crashSeen) {
    return lockedPayout > 0 ? { kind: 'won', amount: lockedPayout } : { kind: 'idle' }
  }
  if (!climbing) return { kind: 'idle' }
  return { kind: 'riding', lockedPayout, activeStakes }
}

export interface ZoneCardStateInputs {
  // Betting window open (race_ready / race_starting).
  isBettingOpen: boolean
  // Builder stake placed on the zone (pre-submit, or queued during live).
  stake: number
  // Amount snapshotted when this round's submission fired (0 = none).
  submittedStake: number
  // Status of the zone's bet this round; undefined = no bet in the store.
  betStatus: string | undefined
  // The multiplier is climbing past 1.00x (event clock — the LOGIC input).
  hasStartedClimbing: boolean
  // The crash EVENT is known backend-side.
  hasCrashed: boolean
  // The crash is visible on THIS viewer's frames (useVisualCrash) — the
  // only signal allowed to flip the card to busted.
  visualCrashed: boolean
  // The zone's live card ever showed a value above 1.00x this round —
  // gates the crash-hold freeze (a bet that never climbed keeps its
  // locked card until the visual flip).
  hasEverClimbed: boolean
  // The bet is still awaiting server acceptance (in the store's
  // unacceptedBets). Gates the busted flip (G10): a bet the server never
  // ACCEPTED must not render as a loss — the settle purge will refund and
  // remove it, and the vanished-bet watcher then frees the zone. Optional:
  // omitted means "assume accepted" (all pre-v12.4 call sites).
  betUnaccepted?: boolean
  // An optimistic cash-out overlay is mounted for this zone (the hook's
  // optCashout — POST in flight). Forces the 'cashing' settling card over
  // 'live' AND 'busted': a crash landing inside the round-trip must not
  // yank the card red under the player's finger (the response handler —
  // or the overlay timeout — resolves it within moments). A CONFIRMED
  // terminal status still wins (branch A): the server's answer is strictly
  // better information than "cashing…".
  optimisticCashout?: boolean
}

export function resolveZoneCardState(i: ZoneCardStateInputs): ZoneCardState {
  const cancelled = i.betStatus === 'cancelled'
  // v12: a CASHED bet frees the zone exactly like cancelled — the win story
  // moves to the derived banner (resolveZoneWinBanner); the card converts to
  // the next-round builder the instant the cashout lands.
  const cashed = i.betStatus !== undefined && CARD_CASHED_STATUSES.includes(i.betStatus)
  const hasBet = i.betStatus !== undefined && !cancelled
  const resolved = i.betStatus !== undefined && CARD_RESOLVED_STATUSES.includes(i.betStatus)

  // A) Free zone — no bet, a cancelled (refunded) one, or a CASHED one.
  //    Cashed/cancelled BYPASS the submittedStake gate (their submission
  //    snapshot still holds the round's stake) — and because this branch
  //    precedes the busted check, a banked zone can never resolve busted
  //    (R6). The staking target: betting-open builder or next-round queue.
  if (cancelled || cashed || (!hasBet && i.submittedStake === 0)) {
    if (i.stake <= 0) return 'empty'
    return i.isBettingOpen ? 'staked' : 'queued'
  }

  // The optimistic settling card — outranks live AND busted while the
  // cash-out POST is unresolved (a confirmed terminal status already exited
  // through branch A above).
  if (i.optimisticCashout) return 'cashing'

  // B) A non-cancelled, non-cashed bet exists.
  if (hasBet) {
    // VISUAL-CRASH GATE: busted only once the viewer has SEEN the crash.
    if (i.visualCrashed && (i.betStatus === 'lost' || !resolved)) {
      // G10: a still-PENDING bet the server never accepted is not a loss —
      // it never existed as a wager. Keep the placing treatment; the settle
      // purge refunds it and the vanished-bet watcher frees the zone. A
      // server-settled 'lost' status always wins (it proves acceptance).
      if (i.betStatus === 'pending' && i.betUnaccepted === true) return 'placing'
      return 'busted'
    }
    const active = !resolved
    // Crash-hold: backend says crashed, viewer hasn't seen it — keep the
    // live look (frozen at the last shown value) for bets that climbed.
    const inCrashHold =
      i.hasCrashed && !i.visualCrashed && i.hasEverClimbed && (active || i.betStatus === 'lost')
    if ((active && i.hasStartedClimbing) || inCrashHold) return 'live'
  }

  // C) Locked / submitted stake card — placing while the POST is in
  //    flight, locked once the bet id landed.
  return i.betStatus === undefined ? 'placing' : 'locked'
}

// The card CONTENT groups that crossfade against each other. States inside
// the same group share content structure (chip stack + amount), so a
// staked → placing → locked progression must NOT re-fire the swap
// animation — only genuine content changes (stake → live → result) do.
export type ZoneCardContentGroup = 'empty' | 'stake' | 'live' | 'busted'

export function zoneCardContentGroup(state: ZoneCardState): ZoneCardContentGroup {
  switch (state) {
    case 'empty':
      return 'empty'
    case 'staked':
    case 'queued':
    case 'placing':
    case 'locked':
      return 'stake'
    case 'live':
    // 'cashing' shares the live group on purpose: the verb line swaps
    // ("Cash out" → "Cashing out…") and the value freezes, but the card
    // must NOT crossfade — it is the same moment continuing, not a new one.
    case 'cashing':
      return 'live'
    case 'busted':
      return 'busted'
  }
}

// ── Live-round control locking (v7; v47 fix) ────────────────────────────
// While a zone's bet is RIDING, its auto-cash-out was already submitted
// with the bet (server-side) — the cash-out toggle + multiplier stepper
// lock (visible, full contrast, not editable). The AUTO toggle stays
// interactive everywhere: it governs the NEXT round's re-bet, so flipping
// it mid-round is meaningful. Zones that only hold a QUEUED next-round
// stake (no riding bet) keep everything editable — nothing of theirs is on
// the wire yet.
//
// v47: the lock keys on the riding fact ALONE, not the panel mode. The
// submission fires ~1.5s into race_starting while the panel is still in
// 'betting' mode — under the old `mode === 'live-collapsed'` gate the
// instrument stayed editable for that window, silently discarding edits
// (the wire carries the submission-time snapshot) and then snapping the
// locked pill back to the stale terms at race_started. `riding` flips
// true the instant the submission fires (resolveZoneRiding), so locking
// on it alone closes the window exactly; pre-submit betting is unaffected
// (riding is false until the POST goes out).
export function isZoneCashoutLocked(riding: boolean): boolean {
  return riding
}

// ── Cash-out control presence (v8) ──────────────────────────────────────
// The footer's [cashout-icon | − x.xx +] instrument has three presentations:
//
//   editable — betting window / live-full / queued-only zone: the full
//              control, toggle + stepper alive.
//   locked   — this zone's bet is riding AND it was submitted WITH an
//              auto-cash-out: the control freezes at full contrast showing
//              the riding terms (v7 lock — a fact, not a broken button).
//   hidden   — this zone's bet is riding and it was submitted with
//              cash-out OFF: there is nothing to show — no terms exist on
//              the wire, so the control disappears entirely (no lock, no
//              ghost) and the zone card carries the whole story.
export type CashoutControlPresence = 'editable' | 'locked' | 'hidden'

export function resolveCashoutControlPresence(
  locked: boolean,
  cashoutOn: boolean
): CashoutControlPresence {
  if (!locked) return 'editable'
  return cashoutOn ? 'locked' : 'hidden'
}

// ── Riding-footer chassis (v9, reshaped v10) ─────────────────────────────
// The per-zone footer row NEVER collapses to a floating icon: every cash-out
// presence maps to a concrete rendering for the footer row, so the auto
// icon always sits on a proper chassis (same structure the betting phase
// uses).
//
//   instrument        — the full editable [cashout-icon | − x.xx +] pill.
//   instrument-locked — the locked pill: cashout-icon | 🔒 x.xx (the riding
//                       bet's terms, full contrast, non-interactive).
//   auto-seat         — the bet rides with cash-out OFF: nothing is on the
//                       wire to state, so the auto icon is SEATED in a
//                       compact quiet pill chassis (same height as the
//                       instrument). The v9 "rail" — a bare hairline off
//                       the icon — read as a glitch; the seat makes the
//                       row read designed, never a floating icon + line.
export type RidingFooterSlot = 'instrument' | 'instrument-locked' | 'auto-seat'

export function resolveRidingFooterSlot(presence: CashoutControlPresence): RidingFooterSlot {
  switch (presence) {
    case 'editable':
      return 'instrument'
    case 'locked':
      return 'instrument-locked'
    case 'hidden':
      return 'auto-seat'
  }
}

// ── Locked cash-out terms (v10) ──────────────────────────────────────────
// What the locked pill STATES while a bet rides. The naive source —
// `myBet.autoCashoutMultiplier` — is lossy: the server-normalized
// `bet:accepted` payload REPLACES the optimistic bet in the store
// (bet.ts `betAccepted`) and `BetMap.fromBetDTO` never maps
// `autoCashoutMultiplier` (BetDTO has no such field), so the field is
// dropped mid-round and the pill used to vanish into the bare rail.
//
// The fix: the hook snapshots the terms ACTUALLY SUBMITTED per zone the
// moment the submission fires (`submittedCashout`, alongside
// `submittedStakes`) — client-owned state that provably survives the round —
// and the display resolves from it here:
//
//   unlocked        → the editable draft (the control is an editor).
//   locked + snapshot → the snapshot: the wire truth, survives replacement.
//   locked, no snapshot, bet carries the field → the server terms
//                     (remount mid-round: the snapshot died with the mount).
//   locked, nothing to prove → no terms claimed (enabled false — the seat
//                     chassis, never invented numbers).
export interface SubmittedCashoutTerms {
  enabled: boolean
  multiplier: number
}

export function resolveLockedCashoutTerms(
  locked: boolean,
  submitted: SubmittedCashoutTerms | undefined,
  betAutoCashoutMultiplier: number | undefined,
  draft: { cashoutEnabled: boolean; cashoutMultiplier: number }
): SubmittedCashoutTerms {
  if (!locked) return { enabled: draft.cashoutEnabled, multiplier: draft.cashoutMultiplier }
  if (submitted !== undefined)
    return { enabled: submitted.enabled, multiplier: submitted.multiplier }
  if (betAutoCashoutMultiplier !== undefined) {
    return { enabled: true, multiplier: betAutoCashoutMultiplier }
  }
  return { enabled: false, multiplier: draft.cashoutMultiplier }
}

// ── Mobile cash-out input mode (v12.3, tweak 5) ──────────────────────────
// On coarse-pointer devices the free-text multiplier field is a liability:
// focusing it pops the virtual keyboard and (on iOS, with a sub-16px font)
// zooms the page into the pill. There the field becomes a read-only DISPLAY
// and the −/+ steppers are the only editor; desktop keeps free typing.
// Pure so the mapping is testable; the caller feeds it the live
// `(pointer: coarse)` media-query fact.
export interface CashoutInputMode {
  readOnly: boolean
  inputMode: 'decimal' | 'none'
}

export function resolveCashoutInputMode(coarsePointer: boolean): CashoutInputMode {
  return coarsePointer
    ? { readOnly: true, inputMode: 'none' }
    : { readOnly: false, inputMode: 'decimal' }
}

// Belt-and-braces guard for the locked state: strip cash-out fields out of a
// zone-auto patch so a locked footer can never mutate the riding bet's
// cash-out terms, while the auto (next-round re-bet) toggle passes through.
export interface ZoneAutoPatchLike {
  autoEnabled?: boolean
  cashoutEnabled?: boolean
  cashoutMultiplier?: number
}

export function filterLockedAutoPatch(
  locked: boolean,
  patch: ZoneAutoPatchLike
): ZoneAutoPatchLike {
  if (!locked) return patch
  // While the zone rides, edits touch only the NEXT round's draft — and the
  // riding terms render from the submission-time snapshot, never the draft,
  // so letting these through cannot change what the server executes:
  //   autoEnabled    — always next-round-only (unchanged).
  //   cashoutEnabled — v41 (owner): toggling auto cash-out OFF while locked
  //                    means "keep it this round, drop it next round" (the
  //                    instrument turns amber + a notice explains).
  // The MULTIPLIER stays stripped: a silently-editable target under a
  // locked pill stating different terms is exactly the confusion the v8
  // lock exists to prevent.
  const safe: ZoneAutoPatchLike = {}
  if (patch.autoEnabled !== undefined) safe.autoEnabled = patch.autoEnabled
  if (patch.cashoutEnabled !== undefined) safe.cashoutEnabled = patch.cashoutEnabled
  return safe
}

// NOTE (v12.1, tweak 1): the v11 could-have-won tease is REMOVED end-to-end
// ("it causes more confusion, keep it simple" — owner). No resolver, no
// threshold constant, no banner line: the win banner states the banked
// outcome only.

// ── Zone win banner (v12 — ask 4) ────────────────────────────────────────
// The cashed zone's win story, DERIVED at render time from the store bet
// (selected through myBetIdRef) + the submission-time cashout snapshot —
// never an event-latched flag, so there is zero new reset surface (R2/R4).
// Money rules (R3, binding):
//
//   - payout = bet.potentialPayout — the BACKEND settlement figure, never
//     the frame beacon, never stake × displayed multiplier;
//   - multiplier resolves EXACTLY like the v11 won card did:
//     cashoutMultiplier (manual REST result) ?? autoCashoutMultiplier
//     (server field, when it survives) ?? the submitted-terms snapshot
//     (the v10 fallback — BetDTO carries no autoCashoutMultiplier, so the
//     bet:accepted replacement strips the field mid-round);
//   - terms unresolvable → banner WITHOUT the @chip. Never invented.
//
// Null when no bet is selected (a mid-round remount killed myBetIdRef —
// accepted degradation: builder-only, no banner) or the status isn't
// cashed. Dies at race_ready for free: the round reset clears myBetIdRef.
export interface WinBannerBetLike {
  status: string
  potentialPayout: number
  cashoutMultiplier?: number
  autoCashoutMultiplier?: number
}

export interface ZoneWinBanner {
  payout: number
  multiplier: number | undefined
}

// v12.2 (minor 5): the banner carries NO caption field — a server-auto-cashed
// bet arrives with status 'won' (no REST round-trip) while a manual cash-out
// lands as 'cashed_out', yet both are the SAME player outcome ("I banked
// before the crash"). The banner ALWAYS wears the localized "Cashed out"
// wordmark (CrashZoneWinBanner renders it); no other won-status surface is
// affected.
export function resolveZoneWinBanner(
  bet: WinBannerBetLike | undefined,
  submittedCashout: SubmittedCashoutTerms | undefined
): ZoneWinBanner | null {
  if (bet === undefined || !CARD_CASHED_STATUSES.includes(bet.status)) return null
  const multiplier =
    bet.cashoutMultiplier ??
    bet.autoCashoutMultiplier ??
    (submittedCashout?.enabled ? submittedCashout.multiplier : undefined)
  return {
    payout: bet.potentialPayout,
    multiplier
  }
}

// ── Bust thump cue (crash loss counterpart) ─────────────────────────────
// The win cue fires PER ZONE at the cashout (CrashBetZone → playEffect
// 'reward')). The bust "thump" is the loss counterpart, fired ONCE at the
// PANEL on the visual-crash rising edge — but only when the player actually
// had money riding into the crash.
//
// "Riding into the crash" is the panel's own riding fact (resolveZoneRiding):
// a zone with a non-terminal bet on the wire (or a fired submission whose id
// hasn't landed) that was NOT freed by a cashout/refund. Such a zone busts to
// 'lost' at the crash → a loser. A zone the player never staked, or cashed
// out / had refunded before the crash, is NOT riding → no thump.
//
// Because it keys on the SINGLE panel-level crash edge (not per zone) and a
// single "any zone riding" boolean, it fires exactly once even when BOTH
// zones bust. Pure so the once-per-crash + only-on-loss decision is testable.
export interface BustThumpInputs {
  // The visual-crash flag THIS render (useVisualCrash — the one-shot panel
  // crash edge the bets bust on).
  visualCrashed: boolean
  // The visual-crash flag the PREVIOUS render (held in a ref by the caller).
  // The thump only fires on the false→true rising edge.
  prevVisualCrashed: boolean
  // Whether the player had at least one zone riding UNCASHED just before this
  // crash — captured by the caller in a ref while !visualCrashed, so it
  // reflects the pre-crash riding state, not the post-crash 'lost' state.
  hadRidingBet: boolean
}

/**
 * Should the bust thump play on THIS render? True exactly on the visual-crash
 * rising edge (prev false → now true) AND only when the player had a riding
 * (uncashed) bet going into the crash. False on every non-edge render, on the
 * falling edge / round reset, and whenever the player had nothing riding
 * (never bet, or cashed out / refunded before the crash). One panel-level
 * decision → fires once even if both zones bust.
 */
export function shouldPlayBustThump(i: BustThumpInputs): boolean {
  // v41 (owner): the crash SOUND fires on EVERY visual-crash rising edge —
  // a silent bust when nobody staked read as broken audio. hadRidingBet no
  // longer gates the cue (it remains in the inputs for the panel's other
  // riding-edge bookkeeping).
  return i.visualCrashed && !i.prevVisualCrashed
}

// ── Live-panel chrome (v12.1 — tweaks 2+3) ──────────────────────────────
// The buildable family: card states whose zone accepts chips right now
// (the staking target — betting-open builder or the live next-round queue).
const BUILDABLE_CARD_STATES: ZoneCardState[] = ['empty', 'staked', 'queued']

/**
 * Whether this zone's builder targets the NEXT round — the per-zone,
 * always-accurate IN-CARD label predicate (v12.1 tweak 2; the v12 floating
 * pill + its resolveLivePanelChrome pill predicate are deleted).
 *
 * Outside the betting-open window every builder-family card builds for the
 * next round: a queued stake AND the empty "Tap to bet" target alike carry
 * the amber NEXT ROUND eyebrow. Riding/resolved cards never do — so a
 * mixed panel (one riding + one building) labels exactly the right card.
 */
export function isNextRoundBuilder(isBettingOpen: boolean, cardState: ZoneCardState): boolean {
  return !isBettingOpen && BUILDABLE_CARD_STATES.includes(cardState)
}

/**
 * Whether the chip strip RESTS (ghost opacity, non-interactive): betting
 * closed and NO zone is buildable — i.e. every card is riding or busted.
 * With ghosting gone (tweak 3) an empty sibling zone is a live builder,
 * so the strip stays active whenever any door to the next round is open.
 */
export function resolveStripResting(isBettingOpen: boolean, cardStates: ZoneCardState[]): boolean {
  if (isBettingOpen) return false
  return !cardStates.some((s) => BUILDABLE_CARD_STATES.includes(s))
}

// ── ONE display clock for player-facing numbers (bug c) ─────────────────
// A published display-clock reading older than this is stale (the hero's
// rAF is parked or unmounted) — fall back to the event clock rather than
// freeze on an old number. 500ms ≈ several missed frames.
export const DISPLAY_CLOCK_FRESH_MS = 500

/**
 * The multiplier the cash-out bar DISPLAYS (its live amount and "@ x.xx").
 *
 * The hero (CrashMultiplierDisplay) runs THE display clock — beacon-preferred
 * (the frame this viewer is actually seeing), event-clock fallback — and
 * publishes it every rAF (useFrameBeacon.publishDisplayMultiplier). The bar
 * must read the SAME clock or the two numbers visibly contradict under
 * stream lag ("1,293.60 @ 9.24x" on the bar vs 7.22x on the hero). Once the
 * crash event is known the value caps at M.
 *
 * MONEY SEMANTICS (unchanged, load-bearing): the cash-out PAYOUT still comes
 * from the backend response — the backend settles on ITS event clock. Since
 * frames trail the backend, the display-clock number shown here is ≤ the
 * multiplier actually paid: surprises only ever favor the player. The event
 * clock also remains the LOGIC input (climb gating, cash-out arming) — only
 * displayed numbers ride the display clock.
 */
export function barDisplayMultiplier(
  displayClock: { value: number; ageMs: number } | null,
  eventClockMultiplier: number,
  crashedMultiplier: number | undefined,
  freshMs: number = DISPLAY_CLOCK_FRESH_MS
): number {
  const base =
    displayClock !== null && displayClock.ageMs < freshMs
      ? displayClock.value
      : eventClockMultiplier
  return typeof crashedMultiplier === 'number' ? Math.min(base, crashedMultiplier) : base
}

/**
 * Whether the cash-out control may be armed.
 *
 * Two clocks disagree about when a round is climbing, and the control has to
 * respect both:
 *
 * - the EVENT clock (`hasStartedClimbing`, from race_started + reveal delay) is
 *   the CEILING. It is what the backend settles on, so arming ahead of it would
 *   offer a cash-out the server would refuse.
 * - the DISPLAY clock (`shownMultiplier`, beacon-preferred) is the FLOOR. The
 *   frames trail the backend by syncDelta (~2.3s in prod), so arming ahead of
 *   it lights the button while the player still sees the 3‥2‥1 countdown —
 *   press it and the server pays ~1.04x on a round that, to them, has not
 *   started. That is what this AND fixes.
 *
 * Conservative in both directions, and it cannot strand the player — but the
 * operative guarantee is resolveDisplayTime's BEACON_DEAD_MS fallback
 * (crashHeroClamp.ts), NOT barDisplayMultiplier's 500ms staleness check: the
 * hero's rAF keeps publishing FRESH readings even off a dead beacon, so the
 * staleness check never fires (that gap is exactly how the 2026-08-17
 * pinned-clock incident stranded cash-out for a whole round). A dead beacon
 * now degrades to the event-clock-only behaviour rather than a button that
 * never lights.
 *
 * LATCHED via shownHighWater (the highest value the frames have shown THIS
 * round). The beacon is parked dead through betting by design, so a round can
 * open on the event-clock fallback and arm at ~1.005; when the beacon then
 * locks mid-climb, tDisplay jumps BACK inside the reveal delay and
 * CrashMultiplierDisplay publishes a hard 1 (the flat arming value). Reading
 * the instantaneous value alone would disarm the button and take the tap
 * target out from under the player's finger for ~1.4s. Arming is one-way
 * within a round: once the frames have shown the climb, it stays armed.
 *
 * The high-water resets when betting reopens, and hasStartedClimbing (the
 * event clock) independently closes the gap between rounds, so a previous
 * round's high-water can never arm the next one.
 *
 * MONEY SEMANTICS (unchanged): the payout still comes from the backend on its
 * own clock, and it is >= the number shown here — surprises only ever favor the
 * player. Waiting for the frames only ever pays MORE.
 */
export function isCashoutArmed(
  hasStartedClimbing: boolean,
  shownMultiplier: number,
  shownHighWater: number = 1
): boolean {
  return hasStartedClimbing && Math.max(shownMultiplier, shownHighWater) > 1
}

export function clampChipToZone(
  s: ZoneStakes,
  z: Zone,
  chip: number,
  lim: StakeLimits
): ZoneStakes {
  const other = z === 'left' ? s.right : s.left
  const zoneRoom = lim.maxBet - s[z]
  const balanceRoom = lim.balance - (s.left + s.right)
  const add = roundTo(
    Math.min(chip, Math.max(0, zoneRoom), Math.max(0, balanceRoom)),
    lim.precision
  )
  if (add <= 0) return s
  return z === 'left'
    ? { left: roundTo(s.left + add, lim.precision), right: other }
    : { left: other, right: roundTo(s.right + add, lim.precision) }
}

export function doubleStakes(s: ZoneStakes, lim: StakeLimits): ZoneStakes {
  let out = { ...s }
  for (const z of ['left', 'right'] as Zone[]) {
    if (out[z] <= 0) continue
    const target = Math.min(out[z] * 2, lim.maxBet)
    const balanceRoom = lim.balance - (out.left + out.right)
    const grown = roundTo(
      out[z] + Math.max(0, Math.min(target - out[z], balanceRoom)),
      lim.precision
    )
    out = z === 'left' ? { ...out, left: grown } : { ...out, right: grown }
  }
  return out
}

// ── Chip-placement verdict (v12.4 ask 7 — G1/G2) ─────────────────────────
// Why a tap refused to stake, so the panel can SAY so (reference parity:
// ChipPlacementPanel refuses with a cancel sound + a reasoned notice, and
// never fires the flying-chip animation on a refused tap). The zone-cap
// check runs BEFORE the balance check so the more specific message wins
// when both would apply — same ordering as the reference's per-option cap.
export type ChipPlacementVerdict =
  | { kind: 'placed'; next: ZoneStakes }
  | { kind: 'max-reached' }
  | { kind: 'insufficient-balance' }

export function classifyChipPlacement(
  s: ZoneStakes,
  z: Zone,
  chip: number,
  lim: StakeLimits
): ChipPlacementVerdict {
  const zoneRoom = lim.maxBet - s[z]
  const balanceRoom = lim.balance - (s.left + s.right)
  if (zoneRoom <= 0) return { kind: 'max-reached' }
  if (balanceRoom <= 0) return { kind: 'insufficient-balance' }
  // v47 — a stake that can NEVER submit refuses at the tap: even spending
  // every remaining unit of balance this zone stays below the table
  // minimum, so "placing" it would animate a success and then nag "Below
  // minimum" every round forever while the doomed stake sits queued.
  // (Chips smaller than minBet remain normal building steps whenever the
  // balance can still finish the climb.)
  if (s[z] + Math.max(0, balanceRoom) < lim.minBet) {
    return { kind: 'insufficient-balance' }
  }
  const next = clampChipToZone(s, z, chip, lim)
  if (next.left === s.left && next.right === s.right) {
    // The rounded add collapsed to 0 — blame the binding (smaller) room.
    return zoneRoom < balanceRoom ? { kind: 'max-reached' } : { kind: 'insufficient-balance' }
  }
  return { kind: 'placed', next }
}

// ── Submit-time adjustment verdicts (v12.4 ask 7 — G4/G5) ────────────────
// resolveSubmittableStakes silently trims/zeroes; these verdicts let the
// hook TELL the player what happened (and stop a starved auto-rebet):
//
//   ok      — submitted exactly as placed (or no stake).
//   trimmed — a reduced amount goes out (balance drift or maxBet clamp).
//   dropped — nothing goes out. reason 'balance' (the trim starved it below
//             the minimum: clear it + stop that zone's auto-rebet, or it
//             re-attempts forever) vs 'min-bet' (the stake was always below
//             the minimum: keep it queued — the R12 clearable-stake rule —
//             but SAY so).
export type SubmitZoneAdjustment =
  | { kind: 'ok' }
  | { kind: 'trimmed'; reason: 'balance' | 'max-bet' }
  | { kind: 'dropped'; reason: 'balance' | 'min-bet' }

export interface SubmitAdjustments {
  resolved: ZoneStakes
  left: SubmitZoneAdjustment
  right: SubmitZoneAdjustment
}

export function resolveSubmitAdjustments(s: ZoneStakes, lim: StakeLimits): SubmitAdjustments {
  const resolved = resolveSubmittableStakes(s, lim)
  const judge = (placed: number, out: number): SubmitZoneAdjustment => {
    if (placed <= 0 || out >= placed) return { kind: 'ok' }
    if (out === 0) {
      return { kind: 'dropped', reason: placed < lim.minBet ? 'min-bet' : 'balance' }
    }
    return {
      kind: 'trimmed',
      reason: placed > lim.maxBet && out === Math.min(placed, lim.maxBet) ? 'max-bet' : 'balance'
    }
  }
  return { resolved, left: judge(s.left, resolved.left), right: judge(s.right, resolved.right) }
}

/**
 * Submit-time safety net (FIX 2 + FIX 3): given the placed stakes and the LIVE
 * limits, return the per-zone amount that is safe to actually submit. Defends
 * against balance drift (auto-rebet) and any stale placement:
 *
 *   - clamp each zone to `maxBet`
 *   - trim so `left + right <= balance` (left-first — left keeps its amount,
 *     right takes the remaining room)
 *   - zero any zone whose resulting amount is below `minBet`
 *
 * A zone that ends up at 0 must not be submitted.
 */
export function resolveSubmittableStakes(s: ZoneStakes, lim: StakeLimits): ZoneStakes {
  const p = lim.precision
  // 1. Per-zone maxBet clamp.
  let left = roundTo(Math.max(0, Math.min(s.left, lim.maxBet)), p)
  let right = roundTo(Math.max(0, Math.min(s.right, lim.maxBet)), p)

  // 2. Combined-balance trim (left-first).
  const budget = Math.max(0, lim.balance)
  if (left > budget) left = roundTo(budget, p)
  const rightRoom = Math.max(0, budget - left)
  if (right > rightRoom) right = roundTo(rightRoom, p)

  // 3. Zero any zone below the minimum bet.
  if (left < lim.minBet) left = 0
  if (right < lim.minBet) right = 0

  return { left, right }
}

// ═══════════════════════════════════════════════════════════════════════
// Crash chip-strip generation (B7 + R2) — parity with ChipPlacementPanel's
// `chips` useMemo. Pure so it's unit-testable; the hook wires it to the
// live config + a GROWING balance high-watermark (B8).
//
//   B7  operator custom-multiplier chips: if the game config ships a
//       non-default chipMultipliers set, build the strip from
//       minBet * multiplier (like the reference), otherwise let
//       generateSmartChips pick clean round values for the balance.
//   R2  offer the exact min bet as an extra leading chip when the smallest
//       clean chip sits above the minimum (prependMinBetChip).
// ═══════════════════════════════════════════════════════════════════════
export interface CrashChipInputs {
  minBet: number
  maxBet: number
  // The player's balance high-watermark (grows, never shrinks — B8). Chips
  // above it are filtered out so the player can't pick an unaffordable stake.
  balance: number
  chipMultipliers: number[]
  targetCount?: number
}

export function generateCrashChips({
  minBet,
  maxBet,
  balance,
  chipMultipliers,
  targetCount = 8
}: CrashChipInputs): number[] {
  if (minBet <= 0 || maxBet <= 0) return [minBet > 0 ? minBet : 0]

  // B7: operator custom multipliers win; else clean balance-fit values.
  const rawValues = hasCustomChipMultipliers(chipMultipliers)
    ? generateChipsFromMultipliers(chipMultipliers, minBet)
    : generateSmartChips({ minBet, maxBet, balance, targetCount })

  // Drop anything the backend would reject (below min / above max) or the
  // player couldn't afford from the high-watermark balance.
  const allFiltered = rawValues.filter((v) => v >= minBet && v <= maxBet && v <= balance)

  // v15 ASK 4 (chip-colour collision): the strip colours chips by their ladder
  // INDEX via `chipStyleForIndex(idx % CHIP_STYLES.length)`, so a ladder longer
  // than the palette wraps and two DISPLAYED chips share a colour (the owner
  // saw a grey 0.7 chip and a grey 20k chip). The final ladder length is
  // `down-sampled count + (1 if the min-bet chip prepends)`. RESERVE the
  // prepend's slot up-front so the down-sample target already accounts for it,
  // and never let the down-sample exceed the palette. This keeps all 8 distinct
  // families (pink 250 / lavender 500 intact) and guarantees no modulo wrap.
  const smallest = allFiltered[0]
  const willPrepend =
    smallest !== undefined && smallest > minBet && minBet >= MIN_BET_CHIP_MIN_VALUE
  // Effective cap for the clean (pre-prepend) chips: the smaller of the
  // requested target and the palette length, minus a slot for the min-bet chip
  // when it will prepend. Never below 1.
  const paletteCap = Math.max(1, CHIP_STYLES.length - (willPrepend ? 1 : 0))
  const effectiveTarget = Math.max(1, Math.min(targetCount, paletteCap))

  // Custom-multiplier configs can ship more than the target — down-sample
  // evenly (keep smallest + largest, even steps between) so the strip stays a
  // balanced spread that fits the palette.
  let filtered = allFiltered
  if (allFiltered.length > effectiveTarget) {
    const step = (allFiltered.length - 1) / (effectiveTarget - 1)
    filtered = []
    for (let i = 0; i < effectiveTarget; i++) {
      const idx = Math.round(i * step)
      const value = allFiltered[idx]
      if (value !== undefined && !filtered.includes(value)) filtered.push(value)
    }
  }

  // Balance below min bet (or nothing fit) → still show the min so the strip
  // isn't empty; the per-tap placement guard blocks an unaffordable bet.
  if (filtered.length === 0) return [minBet]

  // R2: exact min bet as a leading chip when the smallest clean chip is above
  // the minimum. The reserved slot above guarantees this stays within the
  // palette.
  const withMin = prependMinBetChip(filtered, minBet)

  // Hard invariant (dev assert / prod clamp): the strip colours by index modulo
  // the palette, so the ladder must never exceed the palette or a wrap collides
  // two visible chips. In the unlikely event a caller passes a larger
  // targetCount, clamp — keeping the leading min-bet chip and the largest rung.
  if (withMin.length > CHIP_STYLES.length) {
    return withMin.slice(0, CHIP_STYLES.length)
  }
  return withMin
}
